import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import {
  CellClassParams,
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGridPage } from '../../components/data-grid';
import { FilterTextField } from '../../components/filters';
import FilterSelectField from '../../components/filters/filter-select-field.tsx';
import { useInteractiveGrid } from '../../hooks';
import { useAppSelector } from '../../store/hooks';
import { Technician, TechnicianFilters } from '../../types';
import { getStatusLabel } from '../../utils';
import {
  selectAccessTechnicians,
  selectManageManufacturers,
  selectManageTechnicians,
} from '../user';
import { useGetTechniciansQuery, usePrefetch } from './technicians-api-slice';

// Create a TechnicianActionCell component that shows different icons based on user role
const TechnicianActionCell = (params: ICellRendererParams) => {
  const navigate = useNavigate();
  const canManageTechnicians = useAppSelector(selectManageTechnicians);

  return (
    <Tooltip title={!canManageTechnicians ? 'View Technician' : 'Edit Technician'}>
      <IconButton
        size="small"
        onClick={() => navigate(`/technician-profile/${params.data.id}`)}
        aria-label={!canManageTechnicians ? 'view technician' : 'edit technician'}
      >
        {!canManageTechnicians ? (
          <VisibilityIcon fontSize="small" />
        ) : (
          <EditIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const TechnicianManagementPage = () => {
  const hasAccess = useAppSelector(selectAccessTechnicians);
  const prefetchTechnicians = usePrefetch('getTechnicians');
  const navigate = useNavigate();

  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageTechnicians = useAppSelector(selectManageTechnicians);

  const {
    gridContainerRef,
    pagination,
    filters,
    filtersExpanded,
    setFiltersExpanded,
    response: techniciansResponse,
    error,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    onSortChanged,
    handleClearFilters,
    handleNextPageHover,
  } = useInteractiveGrid<TechnicianFilters, Technician>({
    hasAccess,
    prefetchFunction: prefetchTechnicians,
    getDataQuery: useGetTechniciansQuery,
    initialFilters: {
      active: 'true',
    },
    debounceOptions: {
      enabled: true,
      delay: 300,
    },
    spinnerOptions: {
      showSpinnerOnSort: true,
      showSpinnerOnFilter: true,
      showSpinnerOnPageChange: true,
    },
  });

  // Column definitions for AG Grid
  const columnDefs = useMemo<ColDef[]>(() => {
    const columns: ColDef[] = [];
    if (canManageManufacturers) {
      columns.push({
        field: 'manufacturer',
        headerName: 'Manufacturer',
        sortable: true,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value && typeof params.value === 'object' ? params.value.name : params.value,
      });
    }

    columns.push(
      {
        field: 'email',
        headerName: 'Email',
        sortable: true,
        flex: 1.5,
      },
      {
        field: 'firstName',
        headerName: 'First Name',
        sortable: true,
        flex: 1,
      },
      {
        field: 'lastName',
        headerName: 'Last Name',
        sortable: true,
        flex: 1,
      },
      {
        field: 'initials',
        headerName: 'Initials',
        sortable: true,
        width: 100,
      },
      {
        field: 'active',
        headerName: 'Status',
        sortable: true,
        width: 85,
        valueFormatter: (params: ValueFormatterParams) => getStatusLabel(params.data?.active),
        cellStyle: (params: CellClassParams<Technician, string>) => {
          return params.data?.active ? { color: 'green' } : { color: 'red' };
        },
        cellDataType: false,
      },
      {
        headerName: canManageTechnicians ? 'Actions' : 'View',
        width: 80,
        sortable: false,
        cellRenderer: TechnicianActionCell,
      }
    );

    return columns;
  }, [canManageManufacturers, canManageTechnicians]);

  const statusOptions = useMemo(
    () => [
      { value: 'true', label: getStatusLabel(true) },
      { value: 'false', label: getStatusLabel(false) },
    ],
    []
  );

  if (!hasAccess) {
    return null;
  }

  const renderFilterControls = () => {
    return (
      <Grid container spacing={1}>
        {/* Only show manufacturer filter for admin users */}
        {canManageManufacturers && (
          <Grid item xs={12} sm={6} md={2}>
            <FilterTextField
              label="Manufacturer"
              value={filters.manufacturer ?? ''}
              onChange={value => handleFilterChange('manufacturer', value)}
              hasValue={!!filters.manufacturer}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <FilterTextField
            label="Email"
            value={filters.email ?? ''}
            onChange={value => handleFilterChange('email', value)}
            hasValue={!!filters.email}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="First Name"
            value={filters.firstName ?? ''}
            onChange={value => handleFilterChange('firstName', value)}
            hasValue={!!filters.firstName}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterTextField
            label="Last Name"
            value={filters.lastName ?? ''}
            onChange={value => handleFilterChange('lastName', value)}
            hasValue={!!filters.lastName}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5}>
          <FilterSelectField
            label="Status"
            value={filters.active ?? ''}
            onChange={value => handleFilterChange('active', value === '' ? undefined : value)}
            hasValue={filters.active !== undefined && filters.active !== ''}
            options={statusOptions}
            allOptionLabel="All Status"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            size="small"
            sx={{ fontSize: '0.75rem' }}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <DataGridPage<Technician, TechnicianFilters>
      title="Technician Management"
      addButtonText="Add Technician"
      addButtonPath="/technician-profile"
      columnDefs={columnDefs}
      response={techniciansResponse}
      error={error}
      pagination={pagination}
      filters={filters}
      filtersExpanded={filtersExpanded}
      gridContainerRef={gridContainerRef}
      handlePageChange={handlePageChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSortChanged={onSortChanged}
      handleNextPageHover={handleNextPageHover}
      setFiltersExpanded={setFiltersExpanded}
      renderFilterControls={renderFilterControls}
      navigate={navigate}
      hideAddButton={!canManageTechnicians}
    />
  );
};

export default TechnicianManagementPage;
